import {ArgValidator} from "../Utils/ArgValidator";
/**
 * @interface IRequestBase - base request type contains messageId and customData.
 */
export interface IRequestBase {
}

/**
 * @type RawResponseType - raw response type from server (string or JSON object)
 */
export type RawResponseType = string | Object;

/**
 * @interface - IErrorResponse - error response contains info about error like: code, message.
 */
export interface IErrorResponse {
    /**
     * @property code - specific error code (list of generic codes see below)
     * @see @ApiErrorCodes for valid error codes
     */
    code: number;

    /**
     * @property errorId - unique error identifier.
     */
    errorId: string;

    /**
     * @property message - detailed error message
     */
    message: string;

    /**
     * @property messages - list of messages to display to player
     */
    messages?: Array<ErrorMessage>;

    /**
     * @property intervals - list of reality check/session timer intervals
     */
    intervals?: Array<number>;
}

/**
 * @enum ApiClientErrorType - list of possible error types api client can receive
 */
export enum ApiClientErrorType {
    /**
     * @member BadRequest - Http status 400
     */
    BadRequest,

        /**
         * @member Unauthorized - Http status 401
         */
    Unauthorized,

        /**
         * @member NotFound - Http status 404
         */
    NotFound,

        /**
         * @member ServerError - Http status 500
         */
    ServerError,

        /**
         * @member ServiceUnavailable - Http status 503
         */
    ServiceUnavailable,

        /**
         * @member Unknown - unknown error
         */
    Unknown
}

/**
 * @interface IRequestInfo - information about request made by api client
 */
export interface IRequestInfo {
    /**
     * @member request - request data which was send by api client
     */
    request: IRequestBase;

    url: string;

    gameKey: string;
}

export type HandleErrorResponseType = (error: ApiClientError) => void;

/**
 * @interface IApiClientErrorDefinition - definition for API client error
 */
export interface IApiClientErrorDefinition {
    /**
     * @property code - specific error code (list of generic codes see below)
     * @see @ApiErrorCodes for valid error codes
     */
    code: number;

    /**
     * @property httpStatusCode - http status code
     */
    httpStatusCode: number;
}

/**
 * @class ApiClientError - possible api client errors
 */
export class ApiClientError {
    private _httpStatusCode: number;
    private _requestInfo: IRequestInfo;
    private _error: IErrorResponse;
    private _type: ApiClientErrorType;

    public get httpStatusCode(): number {
        return this._httpStatusCode;
    }

    public get requestInfo(): IRequestInfo {
        return this._requestInfo;
    }

    public get error(): IErrorResponse {
        return this._error;
    }

    public get type(): ApiClientErrorType {
        return this._type;
    }

    constructor(httpStatusCode: number, requestInfo: IRequestInfo, error: IErrorResponse) {
        this._type = this.getType(httpStatusCode);
        this._httpStatusCode = httpStatusCode;
        this._requestInfo = requestInfo;
        this._error = error;
    }

    private getType(httpStatusCode: number): ApiClientErrorType {
        var result = ApiClientErrorType.Unknown;

        if (httpStatusCode === 400) {
            result = ApiClientErrorType.BadRequest;
        } else if (httpStatusCode === 401) {
            result = ApiClientErrorType.Unauthorized;
        } else if (httpStatusCode === 404) {
            result = ApiClientErrorType.NotFound;
        } else if (httpStatusCode === 500) {
            result = ApiClientErrorType.ServerError;
        } else if (httpStatusCode === 503) {
            result = ApiClientErrorType.ServiceUnavailable;
        }

        return result;
    }

    /**
     * @method hasDefinition - test if current instance has equal definition as specified one.
     * @param definition - definition to test current instance to.
     * @returns {boolean}
     */
    public hasDefinition(definition: IApiClientErrorDefinition): boolean {
        ArgValidator.ensureIsNotNullOrUndefined(definition, 'definition');

        return this.httpStatusCode === definition.httpStatusCode &&
            this.error && this.error.code === definition.code;
    }
}

/**
 * @type ClientRetryResponseEntry - response entry description for client retry feature
 */
export type ClientRetryResponseEntry = {
    httpStatusCode?: number;
    errorCode?: number;
}

/**
 * @type ErrorMessage - response description for client error message
 */
export type ErrorMessage = {
    type: string;
    title?: string;
    text: string;
    timeout?: number;
    actions: Array<ErrorMessageAction>;
}

/**
 * @type ErrorMessageActions - response description for client error message action
 */
export type ErrorMessageAction = {
    type: string;
    text: string;
    linkUrl: string;
    linkTarget: string;
}


/** WEBPACK FOOTER **
 ** ./Engine/ApiClient/Types.ts
 **/